<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">QR Code 詳情</h1>
      <div class="page-actions">
        <button class="btn btn-default" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
        <button class="btn btn-primary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>

    <div v-else-if="qrcode" class="detail-container">
      <div class="detail-section">
        <h3>基本資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>QR Code 代碼</label>
            <div><code>{{ qrcode.qrcodeCode }}</code></div>
          </div>
          <div class="detail-item">
            <label>設定名稱</label>
            <div>{{ qrcode.configName }}</div>
          </div>
          <div class="detail-item">
            <label>描述</label>
            <div>{{ qrcode.description || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>狀態</label>
            <div>
              <span :class="['badge', qrcode.isActive ? 'badge-success' : 'badge-secondary']">
                {{ qrcode.isActive ? '啟用' : '停用' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3>統計資料</h3>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">{{ qrcode.scanCount || 0 }}</div>
            <div class="stat-label">掃描次數</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ qrcode.addFriendCount || 0 }}</div>
            <div class="stat-label">加好友次數</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ qrcode.bindCount || 0 }}</div>
            <div class="stat-label">綁定次數</div>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3>QR Code 圖片</h3>
        <div class="qrcode-display">
          <img 
            v-if="qrcodeImage" 
            :src="qrcodeImage" 
            alt="QR Code" 
            class="qrcode-image"
          />
          <div v-else class="qrcode-loading">
            <p class="text-muted">載入 QR Code 中...</p>
          </div>
        </div>
        <div class="qrcode-actions">
          <button class="btn btn-sm btn-secondary" @click="downloadQrcode" :disabled="!qrcodeImage">
            <i class="ri-download-line"></i> 下載 QR Code
          </button>
          <button class="btn btn-sm btn-secondary" @click="copyUrl" :disabled="!qrcode?.qrcodeUrl">
            <i class="ri-file-copy-line"></i> 複製連結
          </button>
        </div>
        <div class="qrcode-url" v-if="qrcode?.qrcodeUrl">
          <label>掃描後前往：</label>
          <div class="url-display">
            <code>{{ qrcode.qrcodeUrl }}</code>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-container">
      <p class="error-message">找不到 QR Code 資料</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>
  </div>
</template>

<script>
import lineQrcodeApi from '@/api/modules/lineQrcode.js';

export default {
  name: 'LineQrcodeDetail',
  data() {
    return {
      loading: false,
      error: null,
      qrcode: null,
      qrcodeImage: null
    };
  },
  mounted() {
    this.loadQrcode();
    this.loadQrcodeImage();
  },
  methods: {
    async loadQrcode() {
      this.loading = true;
      this.error = null;
      try {
        const response = await lineQrcodeApi.get(this.$route.params.id);
        if (response && response.success && response.data) {
          this.qrcode = response.data;
        } else {
          this.error = '找不到 QR Code 資料';
        }
      } catch (error) {
        console.error('載入 QR Code 失敗:', error);
        const errorMessage = error?.response?.data?.message || error?.message || '載入失敗';
        this.error = errorMessage;
        this.$message?.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    
    async loadQrcodeImage() {
      try {
        const response = await lineQrcodeApi.getImage(this.$route.params.id);
        if (response && response.success && response.data) {
          this.qrcodeImage = response.data.imageUrl || response.data.image;
        }
      } catch (error) {
        console.error('載入 QR Code 圖片失敗:', error);
        // 圖片載入失敗不影響主要資料顯示
      }
    },
    
    downloadQrcode() {
      if (!this.qrcodeImage || !this.qrcode) return;
      
      const link = document.createElement('a');
      link.href = this.qrcodeImage;
      link.download = `qrcode_${this.qrcode.qrcodeCode || 'qrcode'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$message?.success('QR Code 已下載');
    },
    
    async copyUrl() {
      if (!this.qrcode?.qrcodeUrl) return;
      
      try {
        await navigator.clipboard.writeText(this.qrcode.qrcodeUrl);
        this.$message?.success('連結已複製');
      } catch (error) {
        console.error('複製失敗:', error);
        this.$message?.error('複製失敗');
      }
    },
    
    handleBack() {
      this.$router.push({ name: 'ClientLineQrcodeList' });
    },
    
    handleEdit() {
      this.$router.push({ 
        name: 'ClientEditLineQrcode', 
        params: { id: this.$route.params.id } 
      });
    }
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  margin: 0;
}

.page-actions {
  display: flex;
  gap: 12px;
}

.detail-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.detail-section {
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-section:last-child {
  border-bottom: none;
}

.detail-section h3 {
  font-size: 18px;
  margin-bottom: 16px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.detail-item label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #666;
}

code {
  background-color: #f5f5f5;
  padding: 4px 8px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-default {
  background-color: #f8f9fa;
  color: #333;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.qrcode-display {
  padding: 40px;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

.qrcode-image {
  max-width: 300px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.qrcode-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.qrcode-url {
  margin-top: 16px;
}

.qrcode-url label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #666;
}

.url-display {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  word-break: break-all;
}

.btn-sm {
  padding: 6px 16px;
  font-size: 13px;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-muted {
  color: #6c757d;
}

.loading-container,
.error-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.error-message {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 20px;
}
</style>
